import { format, parseISO } from 'date-fns';
import React, { useMemo } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { theme } from '../../../styles/constants';
import { Container } from './HomeBarChart.styles';

const HomeBarChart = ({ dailyAnalytics }) => {
  const data = useMemo(() => {
    const days = Object.keys(dailyAnalytics);

    return days.map(day => {
      const date = parseISO(day);
      const dayOfWeek = format(date, 'd MMM yy');

      const photos = dailyAnalytics[day].photo.earnings;
      const videos = dailyAnalytics[day].video.earnings;
      const bundles = dailyAnalytics[day].bundle.earnings;
      const subscription = dailyAnalytics[day].subscription.earnings;
      const other = dailyAnalytics[day].other.earnings;
      return {
        name: dayOfWeek,
        data: [photos, videos, bundles, subscription, other],
      };
    });
  }, [dailyAnalytics]);

  const colors = ['#BADAF7', '#97C9F1', '#3E9BE4', '#1672BA', '#092F4D'];

  const options = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'Lexend',
      },
    },
    title: {
      text: '',
    },
    accessibility: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    legend: {
      itemStyle: {
        color: theme.colors.textColor,
        fontSize: 12,
      },
    },
    xAxis: {
      categories: data.map(item => item.name),
      min: data.length > 6 ? data.length - 6 : 0,
      max: data.length - 1,
      scrollbar: {
        enabled: true,
        height: 10,
        trackBorderColor: '#EEF5FD',
        barBorderColor: '#EEF5FD',
        barBackgroundColor: '#EEF5FD',
        trackBackgroundColor: 'transparent',
      },
      labels: {
        style: {
          color: theme.colors.textColor,
          fontSize: 11,
        },
      },
      gridLineColor: '#F3F4F6',
      gridLineDashStyle: 'Dot',
      gridLineWidth: 1,
    },
    yAxis: {
      title: {
        text: '',
      },
      gridLineColor: '#F3F4F6',
      gridLineDashStyle: 'Dot',
      labels: {
        formatter: function ({ value }) {
          return `$${value}`;
        },
        style: {
          color: theme.colors.textColor,
          fontSize: 12,
        },
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false,
        },
      },
      series: {
        borderWidth: 0,
        borderRadius: 5,
        groupPadding: 0,
      },
    },
    colors: colors,
    tooltip: {
      valueDecimals: 2,
      shared: true,
      style: {
        fontSize: 12,
      },
      formatter: function () {
        const total = this.points.reduce((sum, point) => sum + point.y, 0);
        let tooltipText = `<b >${this.point.category}</b><br/><br/>`;

        this.points.forEach(point => {
          const markerStyle = `<span style="color:${point.color}">\u25CF</span>`; // Dot with series color
          tooltipText += `${markerStyle} ${point.series.name}: ${point.y.toFixed(2)}<br/>`;
        });

        tooltipText += `<br/><b>Total: ${total.toFixed(2)}</b>`;
        return tooltipText;
      },
    },
    series: [
      {
        name: 'Photos',
        data: data.map(item => item.data[0]),
        maxPointWidth: 80,
      },
      {
        name: 'Videos',
        data: data.map(item => item.data[1]),
        maxPointWidth: 80,
      },
      {
        name: 'Bundles',
        data: data.map(item => item.data[2]),
        maxPointWidth: 80,
      },
      {
        name: 'Subscriptions',
        data: data.map(item => item.data[3]),
        maxPointWidth: 80,
      },
      {
        name: 'Other',
        data: data.map(item => item.data[4]),
        maxPointWidth: 80,
      },
    ],
  };

  return (
    <Container>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Container>
  );
};

export default HomeBarChart;
